<script setup lang="ts">
   import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';
   import { ChevronRightIcon } from '@heroicons/vue/20/solid';

   const { clientInformation, clientProfile, isClient } = storeToRefs(useClientInfo());
   const { selectTherapistRecommended } = usePlanStore();
   const { setTherapistReassign } = useClientInfo();
   const { track } = useSegment();

   const redirect = (action: boolean) => {
      useClientInfo().setRedirectionUrl(true);
      if (action) {
         track('Change Therapist Completed', {
            current_therapist_id: clientInformation?.value?.therapist?.id,
            retake_assessment: true,
         });
         navigateTo('/auth/client/register/therapy-reasons');
      } else {
         track('Change Therapist Completed', {
            current_therapist_id: clientInformation?.value?.therapist?.id,
            retake_assessment: false,
         });
         navigateTo('/therapist-recommendations?redirect=profile');
      }
   };
   const changeTherapistModal = ref(false);
   const openPrevTherapistSelection = ref(false);
   const openWelcomeBack = ref(false);
   const { t } = useI18n();
   const { therapistStatus } = storeToRefs(useClientInfo());
   const changeTherapistFlow = () => {
      if (
         therapistStatus?.value?.was_unassigned &&
         therapistStatus?.value?.previous_therapist?.id &&
         therapistStatus?.value?.previous_therapist?.active !== false
      ) {
         openPrevTherapistSelection.value = true;
      } else {
         changeTherapistModal.value = true;
      }
   };
   const matchNewTherapistFlow = () => {
      openPrevTherapistSelection.value = false;
      track('Change Therapist Started', {
         current_therapist_id: clientInformation?.value?.therapist?.id,
      });
      changeTherapistModal.value = true;
   };
   const loadingState = ref(false);
   const rematchError = ref(null);
   const ChangeTherapist = async () => {
      loadingState.value = true;
      rematchError.value = null;
      let { data, error } = await selectTherapistRecommended(
         therapistStatus?.value?.previous_therapist?.id,
      );
      if (data?.value) {
         setTherapistReassign(true);
         openPrevTherapistSelection.value = false;
         openWelcomeBack.value = true;
         loadingState.value = false;
      }
      if (error?.value) {
         rematchError.value = error?.value?.data?.message;
         loadingState.value = false;
      }
   };
</script>
<template>
   <div v-if="isClient">
      <div
         v-if="clientInformation?.therapist?.id"
         id="activeTherapistName"
         class="ps-6 pe-1 py-9 border-b border-gray-25 flex gap-4 align-top"
      >
         <div>
            <BaseAvatar
               :image-id="clientInformation?.therapist?.image"
               role="client"
               style="min-width: 50px; min-height: 50px; max-width: 50px; max-height: 50px"
               classes="rounded-full object-cover"
               :alt="`${clientInformation?.therapist?.name} Profile Picture`"
            />
         </div>
         <div @click="navigateTo('/client/therapist/profile')">
            <p
               class="text-sm max-w-32 cursor-pointer whitespace-nowrap font-bold truncate"
               :title="clientInformation?.therapist?.name"
            >
               {{ clientInformation?.therapist?.name }}
            </p>
            <p class="text-xs text-gray-300 mt-1">
               {{ clientInformation?.therapist.title }}
            </p>
            <p
               v-if="clientInformation?.therapist?.experience"
               class="py-1 flex gap-1 items-center mt-2 px-2 text-xs text-secondary-500 border rounded-full border-secondary-500"
            >
               <CheckCircleIcon class="w-4 h-4" />
               {{ clientInformation?.therapist?.experience }}
               <span>{{ t('header.yearsInPractice') }}</span>
            </p>
         </div>
      </div>
      <div v-else class="px-6 py-7 border-b border-gray-25">
         <ClientChangeTherapistModal @success="openWelcomeBack.value = true" />
      </div>
      <BaseDialog size="sm" v-model:open-dialog="changeTherapistModal">
         <template #header></template>
         <div class="grid grid-cols-12">
            <div class="col-span-12 flex gap-3 mb-6">
               {{ t('therapistProfile.changeTherapistTitle') }}
               <XMarkIcon
                  @click="changeTherapistModal = false"
                  class="h-12 w-12 cursor-pointer"
               ></XMarkIcon>
            </div>
            <div class="col-span-12 gap-5 flex justify-between">
               <BaseButton @click="redirect(false)" block variant="outlined" color="primary-500">
                  {{ t('No') }}
               </BaseButton>
               <BaseButton @click="redirect(true)" block color="primary-500">
                  {{ t('Yes') }}
               </BaseButton>
            </div>
         </div>
      </BaseDialog>
      <BaseDialog size="md" :no-close="true" v-model:open-dialog="openPrevTherapistSelection">
         <template #header></template>
         <div class="text-center">
            <div class="text-2xl mt-6 font-medium">Match with a Therapist</div>
            <div class="my-2 text-sm text-gray-600">
               Your previous therapist {{ therapistStatus?.previous_therapist?.name }} is unassigned
               due in activity
            </div>
            <div class="my-6">
               <BaseAvatar
                  classes="h-10 w-10 md:h-20 md:w-20 mx-auto rounded-full mb-4"
                  :image-id="therapistStatus?.previous_therapist?.image"
               />
            </div>
            <div class="text-gray-600 text-sm mb-6">
               You can rematch with your previous therapist or get matched to new therapist
            </div>
            <div
               v-if="rematchError"
               class="flex gap-2 mb-4 rounded bg-primary-50 border p-2 text-left border-primary-300"
            >
               <div>
                  <InformationCircleIcon class="text-primary-400 h-6 w-6" />
               </div>
               <div class="text-primary-400 text-sm">
                  {{ t('matchTherapistErrorTitle') }}.
                  <span class="text-black text-sm"> {{ t('matchTherapistErrorSubtitle') }}</span>
               </div>
            </div>
            <div class="flex justify-evenly mb-2 items-center">
               <BaseButton
                  :loading="loadingState"
                  @click="matchNewTherapistFlow"
                  :variant="rematchError ? 'solid' : 'outlined'"
                  color="primary-500"
               >
                  Match with a new therapist
               </BaseButton>
               <BaseButton
                  v-if="!rematchError"
                  :loading="loadingState"
                  @click="ChangeTherapist"
                  color="primary-500"
               >
                  Rematch with previous therapist
               </BaseButton>
            </div>
         </div>
      </BaseDialog>
      <BaseDialog size="md" :no-close="true" v-model:open-dialog="openWelcomeBack">
         <template #header></template>
         <div class="text-center">
            <div class="text-2xl mt-6 font-medium">Welcome back</div>
            <div class="my-2 text-sm text-gray-600">
               Your therapist
               <span class="text-primary-500">{{ therapistStatus?.previous_therapist?.name }}</span>
               is ready to meet you!
            </div>
            <div class="flex items-center gap-2 my-10 justify-center">
               <BaseAvatar
                  :image-id="clientProfile?.image"
                  role="client"
                  classes="rounded-full object-cover w-18 md:w-24 h-18 md:h-24"
                  :alt="`${clientProfile?.first_name} Profile Picture`"
               />
               <ChevronRightIcon class="text-primary-500 h-6 w-6"></ChevronRightIcon>
               <ChevronRightIcon class="text-primary-500 h-6 w-6"></ChevronRightIcon>
               <ChevronRightIcon class="text-primary-500 h-6 w-6"></ChevronRightIcon>
               <BaseAvatar
                  :image-id="clientInformation?.therapist?.image"
                  role="client"
                  classes="rounded-full object-cover w-18 md:w-24 h-18 md:h-24"
                  :alt="`${clientProfile?.first_name} Profile Picture`"
               />
            </div>
            <div class="text-gray-600 text-sm mb-6">
               You’ve taken a brave first step towards feeling better today.We/ll do everything we
               can to help and support you on your mental wellness journey.
            </div>
            <div class="flex justify-evenly mb-2 items-center">
               <BaseButton
                  block
                  :loading="loadingState"
                  @click="openWelcomeBack = false"
                  color="primary-500"
               >
                  Continue
               </BaseButton>
            </div>
         </div>
      </BaseDialog>
   </div>
</template>
